/**
 * Friendly error-extraction helper for API failures.
 *
 * Backend ships TWO response shapes for errors:
 *   • Regular FastAPI HTTPException → { detail: "Foo" }
 *   • Plan-limit (PlanLimitExceeded) → { error: "plan_limit_exceeded", message: "Multi Workspace isn't included in Free", kind, key, ... }
 *
 * The plan-limit case has no `detail`, so call sites that do
 *   toast.error(e?.response?.data?.detail || "Foo failed")
 * collapse to the meaningless fallback — burying the actual reason.
 *
 * This helper walks the known shapes in priority order and falls back
 * to a caller-supplied default. ALWAYS prefer this over inline
 * `data?.detail || "..."` in catch blocks. iter-121.22.
 */
export function friendlyApiError(err, fallback = "Request failed") {
    const data = err?.response?.data;
    if (data && typeof data === "object") {
        // FastAPI HTTPException
        if (typeof data.detail === "string" && data.detail.trim()) return data.detail;
        // Pydantic validation error → { detail: [{msg, loc}, ...] }
        if (Array.isArray(data.detail) && data.detail[0]?.msg) return data.detail[0].msg;
        // PlanLimitExceeded
        if (typeof data.message === "string" && data.message.trim()) return data.message;
        // Catch-all error string (some routes use `error` key)
        if (typeof data.error === "string" && data.error.trim() && data.error !== "plan_limit_exceeded") return data.error;
    }
    // Network-level error
    if (err?.message && err.message !== "Network Error") return err.message;
    return fallback;
}

export default friendlyApiError;
